import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { FaSearch, FaDownload, FaPlus, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";

import TablaMiembros from "../components/miembros/TablaMiembros";
import ModalCrearMiembro from "../components/miembros/ModalCrearMiembro";
import ModalEditarMiembro from "../components/miembros/ModalEditarMiembro";
import ModalEncuesta from "../components/miembros/ModalEncuesta";

const API = "/api/miembros";
const REGISTROS_POR_PAGINA = 10;

export default function Miembros() {

  const { token } = useContext(AuthContext);

  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const [busqueda, setBusqueda] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("activos");
  const [pagina, setPagina] = useState(1);

  const [showCrearModal, setShowCrearModal] = useState(false);
  const [editarMiembro, setEditarMiembro] = useState(null);

  // 🆕 ENCUESTA
  const [encuestaActiva, setEncuestaActiva] = useState(null);
  const [miembroEncuesta, setMiembroEncuesta] = useState(null);
  const [respondidos, setRespondidos] = useState([]);

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  // =========================
  // 📌 MIEMBROS
  // =========================
  const fetchMiembros = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}?estatus=${estatusFiltro}`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      setMiembros(Array.isArray(data) ? data : []);

    } catch (error) {
      console.error("Error cargando miembros:", error);
      mostrarToast("Error cargando miembros", "error");
    } finally {
      setLoading(false);
    }
  }, [token, estatusFiltro]);

  // =========================
  // 📌 ENCUESTA ACTIVA
  // =========================
  const fetchEncuestaActiva = useCallback(async () => {
    try {
      const res = await fetch("/api/encuesta/activa", {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      setEncuestaActiva(data);

      if (data) {
        fetchRespondidos(data.id);
      }

    } catch (error) {
      console.error("Error cargando encuesta activa:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // =========================
  // 📌 RESPONDIDOS
  // =========================
  const fetchRespondidos = async (encuestaId) => {
    try {
      const res = await fetch(
        `/api/encuesta/respondidos/${encuestaId}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();
      setRespondidos(data.map(r => r.id_miembro));

    } catch (error) {
      console.error("Error cargando respondidos:", error);
    }
  };

  // =========================
  // 📌 INIT
  // =========================
  useEffect(() => {
    if (!token) return;
    fetchMiembros();
    fetchEncuestaActiva();
  }, [token, fetchMiembros, fetchEncuestaActiva]);

  // =========================
  // 📌 ACTIVAR / DESACTIVAR
  // =========================
  const cambiarEstado = async (miembro) => {
    const activar = miembro.status === false;
    try {
      const res = await fetch(`${API}/${miembro.id_miembro}${activar ? "/reactivar" : ""}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        mostrarToast("Error actualizando el estatus del miembro", "error");
        return;
      }

      mostrarToast(activar ? "Miembro reactivado" : "Miembro desactivado");
      fetchMiembros();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  // =========================
  // 📌 FILTRO + PAGINACIÓN
  // =========================
  const miembrosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return miembros;
    return miembros.filter(
      (m) =>
        `${m.nombre} ${m.paterno || ""} ${m.materno || ""}`.toLowerCase().includes(termino) ||
        (m.email && m.email.toLowerCase().includes(termino))
    );
  }, [miembros, busqueda]);

  const totalPaginas = Math.ceil(miembrosFiltrados.length / REGISTROS_POR_PAGINA);
  const inicio = (pagina - 1) * REGISTROS_POR_PAGINA;
  const fin = inicio + REGISTROS_POR_PAGINA;
  const miembrosPagina = miembrosFiltrados.slice(inicio, fin);
  const inicioRango = miembrosFiltrados.length === 0 ? 0 : inicio + 1;
  const finRango = Math.min(fin, miembrosFiltrados.length);

  // =========================
  // 📌 EXPORTAR CSV
  // =========================
  const exportarCSV = () => {
    setExportando(true);
    try {
      const encabezados = ["Nombre Completo", "Dirección", "Teléfonos", "Correo", "Estatus"];
      const lineas = [encabezados.join(",")];

      miembrosFiltrados.forEach((m) => {
        const fila = [
          `${m.nombre} ${m.paterno || ""} ${m.materno || ""}`.trim(),
          m.direccion || "",
          m.telefonos || "",
          m.email || "",
          m.status === false ? "Inactivo" : "Activo",
        ];
        lineas.push(fila.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
      });

      const csv = "﻿" + lineas.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "miembros.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExportando(false);
    }
  };

  // =========================
  // 🎨 UI
  // =========================
  return (
    <div className="p-6 bg-slate-50">

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Miembros</h1>
          <p className="text-sm text-slate-500 mt-0.5">Directorio de miembros de la iglesia.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarCSV}
            disabled={exportando}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaDownload className="text-xs" /> {exportando ? "Exportando..." : "Exportar"}
          </button>

          <button
            onClick={() => setShowCrearModal(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <FaPlus className="text-xs" /> Nuevo miembro
          </button>
        </div>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* BARRA DE FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por nombre o correo..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPagina(1);
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => {
              setEstatusFiltro(e.target.value);
              setPagina(1);
            }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      <TablaMiembros
        miembros={miembrosPagina}
        loading={loading}
        onEditar={(miembro) => setEditarMiembro(miembro)}
        onEncuesta={(miembro) => setMiembroEncuesta(miembro)}
        onCambiarEstado={cambiarEstado}
        encuestaActiva={encuestaActiva}
        respondidos={respondidos}
      />

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {miembrosFiltrados.length === 0
            ? "Sin resultados"
            : `Mostrando ${inicioRango} a ${finRango} de ${miembrosFiltrados.length} resultados`}
        </span>

        {totalPaginas > 1 && (
          <div className="flex items-center gap-2">
            <button
              disabled={pagina === 1}
              onClick={() => setPagina(pagina - 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
            >
              <FaChevronLeft className="text-xs" />
            </button>

            {Array.from({ length: totalPaginas }, (_, i) => (
              <button
                key={i}
                onClick={() => setPagina(i + 1)}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-semibold transition ${
                  pagina === i + 1
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(pagina + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* MODAL CREAR */}
      {showCrearModal && (
        <ModalCrearMiembro
          token={token}
          onClose={() => setShowCrearModal(false)}
          onSave={fetchMiembros}
        />
      )}

      {/* MODAL EDITAR */}
      {editarMiembro && (
        <ModalEditarMiembro
          token={token}
          miembro={editarMiembro}
          onClose={() => setEditarMiembro(null)}
          onSave={fetchMiembros}
        />
      )}

      {/* 🆕 MODAL ENCUESTA */}
      {miembroEncuesta && encuestaActiva && (
        <ModalEncuesta
          token={token}
          miembro={miembroEncuesta}
          encuesta={encuestaActiva}
          onClose={() => setMiembroEncuesta(null)}
        />
      )}

    </div>
  );
}
